import { useEffect, useRef, useState } from 'react';
import { useCheckout } from '@/components/CheckoutContext';
import { X, Shield, Sparkles, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- Configurable constants ----------
// Real merchant UPI QR (BHIM/UPI compatible — scan with any UPI app).
// Encodes payee designcarat426401.rzp@rxairtel · customer enters ₹999.
const UPI_QR_IMAGE = '/payment-qr.jpeg';

const WHATSAPP_BASE =
  'https://wa.me/+916352442321?text=Hi!%20I%20just%20paid%20%E2%82%B9999%20for%20the%20DesignCarat%20STL%20Bundle.%20My%20UTR%20number%20is%20';

const PAYMENT_METHODS = [
  { label: 'GPay', tone: 'from-[#4285F4] to-[#34A853]' },
  { label: 'UPI', tone: 'from-[#5F259F] to-[#FD3E81]' },
  { label: 'Paytm', tone: 'from-[#00BAF2] to-[#002970]' },
  { label: 'PhonePe', tone: 'from-[#5F259F] to-[#7B2FBF]' },
];
// -------------------------------------------

export default function UpiCheckoutModal() {
  const { isOpen, close, source } = useCheckout();
  const [utr, setUtr] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Reset state + focus input when opening
  useEffect(() => {
    if (isOpen) {
      setUtr('');
      setError('');
      setSubmitting(false);
      // slight delay so the modal mount animation finishes
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Lock body scroll + close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = utr.trim();
    if (trimmed.length < 6) {
      setError('Please enter a valid UTR (at least 6 characters).');
      inputRef.current?.focus();
      return;
    }
    setError('');
    setSubmitting(true);

    // 1) Fire-and-forget: save pending order to backend FIRST.
    //    Using a non-awaited axios call keeps us inside the synchronous
    //    user-gesture context so the window.open() below is not blocked
    //    by popup blockers. The request has already left the browser
    //    by the time WhatsApp is opened.
    try {
      axios
        .post(`${API}/orders/utr`, { utr: trimmed, source: source || 'cta' })
        .then((resp) => {
          // Tag the lead with the resulting order_id in case you want
          // to inspect it later in DevTools / analytics.
          if (resp?.data?.order_id && typeof window !== 'undefined') {
            window.__lastUtrOrderId = resp.data.order_id;
          }
        })
        .catch((err) => {
          // Non-blocking — log but do not interrupt UX.
          // Admin can still recover via the UTR shared on WhatsApp.
          // eslint-disable-next-line no-console
          console.warn('UTR order save failed:', err?.message || err);
        });
    } catch (_) {
      // swallow — never block WhatsApp redirect
    }

    // 2) Open WhatsApp instantly in a new tab with the UTR pre-filled.
    const url = `${WHATSAPP_BASE}${encodeURIComponent(trimmed)}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    // 3) Meta Pixel — Purchase. Note: this is a "purchase intent" signal at
    //    UTR submit time. Final verification happens manually on WhatsApp,
    //    but the user has paid via UPI by this point so it's a strong signal
    //    for ad-platform optimization.
    try {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          value: 999,
          currency: 'INR',
          content_name: 'DesignCarat 4,000+ STL Bundle',
          content_category: 'digital_bundle',
          content_ids: ['designcarat_bundle'],
          contents: [{ id: 'designcarat_bundle', quantity: 1 }],
          num_items: 1,
        });
      }
    } catch (_) {
      // never block UX on analytics
    }

    // Tiny delay before resetting so the button's "submitting" state
    // is visible for a moment (nice feedback), then close the modal.
    setTimeout(() => {
      setSubmitting(false);
      close();
    }, 600);
  };

  return (
    <div
      data-testid="upi-checkout-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fadeIn"
      style={{
        background: 'radial-gradient(120% 120% at 50% 0%, rgba(20,15,10,0.55), rgba(0,0,0,0.82))',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        data-testid="upi-checkout-modal"
        className="relative w-full max-w-md sm:max-w-lg overflow-hidden rounded-3xl animate-slideUp"
        style={{
          background:
            'linear-gradient(180deg, rgba(26,18,12,0.92) 0%, rgba(12,8,6,0.98) 100%)',
          border: '1px solid rgba(253,195,161,0.22)',
          boxShadow:
            '0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 120px -30px rgba(253,195,161,0.35)',
        }}
      >
        {/* Gold glow accents */}
        <div
          className="pointer-events-none absolute -top-24 -left-24 w-64 h-64 rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(253,195,161,0.6), transparent 60%)' }}
        />
        <div
          className="pointer-events-none absolute -bottom-24 -right-24 w-64 h-64 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,200,120,0.5), transparent 60%)' }}
        />

        {/* Close */}
        <button
          onClick={close}
          data-testid="upi-checkout-close"
          aria-label="Close checkout"
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full transition-all"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.75)',
          }}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="relative px-7 pt-8 pb-5 text-center">
          <span
            data-testid="upi-secure-badge"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{
              background: 'rgba(253,195,161,0.12)',
              border: '1px solid rgba(253,195,161,0.28)',
              color: '#FDC3A1',
            }}
          >
            <Shield className="w-3 h-3" />
            Secure Checkout
          </span>
          <h2
            className="mt-4 font-heading font-black text-2xl sm:text-[26px] leading-tight text-white"
            data-testid="upi-modal-heading"
          >
            Final Step to Access <span className="inline-block">🚀</span>
          </h2>
          <p
            className="mt-2.5 font-body text-[13px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            Scan the QR code below to pay{' '}
            <span className="text-white font-semibold">₹999</span> for the DesignCarat 4,000+ STL
            Bundle and send the UTR to activate your access.
          </p>
        </div>

        {/* QR code area */}
        <div className="relative px-7 pb-5">
          <div className="relative mx-auto w-[260px] h-[260px] sm:w-[300px] sm:h-[300px]">
            {/* Glow */}
            <div
              className="absolute inset-0 rounded-3xl blur-2xl opacity-70"
              style={{
                background:
                  'conic-gradient(from 0deg, rgba(253,195,161,0.55), rgba(255,220,150,0.25), rgba(253,195,161,0.55))',
              }}
            />
            {/* QR card */}
            <div
              className="relative w-full h-full rounded-2xl p-1.5 flex items-center justify-center"
              style={{
                background: '#FFFFFF',
                boxShadow:
                  '0 0 0 1px rgba(253,195,161,0.35), 0 20px 50px -10px rgba(253,195,161,0.35)',
              }}
            >
              <img
                data-testid="upi-qr-image"
                src={UPI_QR_IMAGE}
                alt="Scan to pay ₹999 via UPI — BHIM, GPay, PhonePe, Paytm"
                className="w-full h-full object-contain rounded-xl"
                loading="eager"
                draggable={false}
              />
            </div>
          </div>

          {/* Merchant note */}
          <p
            data-testid="upi-merchant-note"
            className="mt-4 text-center font-body text-[11px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            Pay <span className="text-white font-semibold">₹999</span> to{' '}
            <span className="text-white font-semibold">Design Carat</span>
            <br className="hidden sm:block" /> using any UPI app — copy your UTR / Reference No.
            after payment.
          </p>

          {/* Payment method logos */}
          <div
            className="mt-4 flex flex-wrap items-center justify-center gap-2"
            data-testid="upi-payment-methods"
          >
            {PAYMENT_METHODS.map((m) => (
              <span
                key={m.label}
                className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-wide text-white bg-gradient-to-r ${m.tone} shadow-sm`}
                style={{ boxShadow: '0 4px 12px -4px rgba(0,0,0,0.6)' }}
              >
                {m.label}
              </span>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div
          className="mx-7 my-1 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(253,195,161,0.35), transparent)',
          }}
        />

        {/* UTR form */}
        <form onSubmit={handleSubmit} className="relative px-7 py-6 space-y-4">
          <div>
            <label
              htmlFor="utr-input"
              className="block font-body text-[10px] font-bold uppercase tracking-[0.18em] mb-2"
              style={{ color: 'rgba(253,195,161,0.9)' }}
            >
              Enter Transaction UTR Number
            </label>
            <input
              id="utr-input"
              ref={inputRef}
              data-testid="utr-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={utr}
              onChange={(e) => {
                setUtr(e.target.value);
                if (error) setError('');
              }}
              placeholder="e.g. 123456789012"
              className="w-full px-4 py-3.5 rounded-xl font-mono text-sm text-white placeholder:text-white/30 outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: error
                  ? '1px solid rgba(248,113,113,0.55)'
                  : '1px solid rgba(255,255,255,0.1)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.border = '1px solid rgba(253,195,161,0.55)';
                e.currentTarget.style.boxShadow =
                  '0 0 0 3px rgba(253,195,161,0.15), inset 0 1px 0 rgba(255,255,255,0.04)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = error
                  ? '1px solid rgba(248,113,113,0.55)'
                  : '1px solid rgba(255,255,255,0.1)';
                e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.04)';
              }}
            />
            {error && (
              <p
                data-testid="utr-error"
                className="mt-2 font-body text-[11px] text-red-400"
              >
                {error}
              </p>
            )}
            <p className="mt-2 font-body text-[10.5px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
              UTR appears in your bank/UPI app after the payment is completed. It's usually 12
              digits long.
            </p>
          </div>

          <button
            type="submit"
            data-testid="upi-submit-whatsapp"
            disabled={submitting}
            className="group relative w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-heading font-black text-sm tracking-wide overflow-hidden transition-transform active:scale-[0.99] disabled:opacity-80 disabled:cursor-wait"
            style={{
              color: '#1a0f06',
              background:
                'linear-gradient(180deg, #FFE0B0 0%, #FDC3A1 55%, #E8A174 100%)',
              boxShadow:
                '0 16px 40px -10px rgba(253,195,161,0.55), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 0 rgba(0,0,0,0.08)',
            }}
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-[#1a0f06]/30 border-t-[#1a0f06] animate-spin" />
                OPENING WHATSAPP…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                SUBMIT VIA WHATSAPP
                <ExternalLink className="w-4 h-4 opacity-80" />
              </>
            )}
          </button>

          <p
            className="text-center font-body text-[10px] leading-relaxed pt-1"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            We'll verify your payment and send your Google Drive access link within{' '}
            <span className="text-white/70 font-semibold">60 seconds</span>.
          </p>
        </form>
      </div>

      {/* Scoped animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px) scale(0.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
        .animate-fadeIn { animation: fadeIn 180ms ease-out both; }
        .animate-slideUp { animation: slideUp 260ms cubic-bezier(0.25,0.46,0.45,0.94) both; }
      `}</style>
    </div>
  );
}
